import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useMotionValueEvent, useReducedMotion, useScroll, useTransform, type MotionValue } from 'framer-motion';
import { ArrowRightIcon, VideoIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { MediaView } from '../shared/MediaView';
import { useOverlayActions } from '../../contexts/StoreContext';
import { founder } from '../../data/content';
import { usePages, useSite } from '../../contexts/SiteContext';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { ease } from '../../core/format/motion';
import { mediaOr } from '../../core/rules/media';
import type { Media } from '../../core/types/media';
import { fill } from '../../utils/copy';
import { belowHeader, eyebrowCls, quietLink, sectionHeading } from '../../ui/sections';
import { segmentFill, stepAt, trackHeight } from './storyScene';

/** Lager dan dit (telefoon liggend) is vastzetten krap: dan staan de stappen gewoon onder elkaar. */
const TALL_ENOUGH = '(min-height: 560px)';

/**
 * "Zo werk ik": de werkwijze als vastgezette scène, op salie. Het hart van het verhaal.
 *
 * - De sectie schuift als een kaart óver de vorige heen (ronde bovenhoeken, zachte schaduw).
 * - Zodra de scène het scherm vult, zet hij zich vast (net onder de header). Scrollen loopt dan
 *   door Kijken, Kiezen, Opbouwen: foto en tekst wisselen samen, drie balkjes tonen waar je bent.
 *   Op elk scherm hetzelfde; computer foto links en tekst rechts, smaller foto boven en tekst eronder.
 * - Minder beweging of een heel laag scherm: niets vastgezet, de stappen onder elkaar met hun foto.
 * - Daarna de volgende stap: het huidadvies (dat ís Kijken), met een afspraak en het consult rustig eronder.
 *
 * Beelden per stap uit Paginateksten (home.story.steps[].media); leeg = het salonbeeld, het
 * portret met producten, het portret buiten. Shell op salie 5,4:1.
 */
export function HomeStory() {
  const pages = usePages();
  const {
    policy
  } = useSite();
  const {
    openBooking,
    openQuiz
  } = useOverlayActions();
  const reduce = useReducedMotion();
  const tall = useMediaQuery(TALL_ENOUGH);
  const copy = pages.home.story;
  const steps = copy.steps;
  const pinned = !reduce && tall && steps.length > 1;
  const fallbacks: Media[] = [mediaOr(copy.media, {
    image: founder.atWork,
    alt: `${founder.name} tijdens een behandeling in de salon`
  }), {
    image: founder.withProducts,
    alt: `${founder.name} met de producten die ze kiest`,
    focus: {
      x: 50,
      y: 35
    }
  }, {
    image: founder.portraitPalm,
    alt: founder.name,
    focus: {
      x: 50,
      y: 30
    }
  }];
  const imageFor = (i: number): Media => mediaOr(steps[i]?.media ?? {
    image: '',
    alt: ''
  }, fallbacks[i % fallbacks.length]);
  return <section className="relative z-raised -mt-8 rounded-t-[2rem] bg-sage text-shell shadow-[0_-24px_48px_-28px_rgba(36,31,28,0.35)] lg:-mt-14 lg:rounded-t-[3rem]" aria-labelledby="home-story" data-id="element-7503" data-mp="3m9x8xvnb5wdu">
      <div className="mx-auto max-w-content px-6 pt-20 sm:pt-24 lg:px-10 lg:pt-32" data-id="element-7504" data-mp="1khshel9q113n">
        <div className="grid gap-8 lg:grid-cols-12 lg:gap-16" data-id="element-7505" data-mp="1sxi2tupsvf1w">
          <div className="lg:col-span-7" data-id="element-7506" data-mp="1wz392x7twv34">
            {copy.eyebrow && <p className={`${eyebrowCls} text-shell`} data-id="element-7507" data-mp="1wxijzn4aa4cn">{fill(copy.eyebrow)}</p>}
            <h2 id="home-story" className={`mt-5 ${sectionHeading}`} data-id="element-7508" data-mp="21qv1ldajpavw">
              <HeadingText heading={copy.heading} accentBlock accentClassName="text-blush" data-id="element-7509" data-mp="26od3wku24rux" />
            </h2>
          </div>
          {copy.body && <p className="max-w-md text-[17px] leading-relaxed text-shell lg:col-span-5 lg:self-end" data-id="element-7510" data-mp="1a0ys50zclj38">{fill(copy.body)}</p>}
        </div>
      </div>

      {steps.length > 0 && (pinned ? <PinnedSteps steps={steps.map((s, i) => ({
      title: fill(s.title),
      body: fill(s.body),
      media: imageFor(i)
    }))} data-id="element-7511" data-mp="1iyism7pdzbcs" /> : <StackedSteps steps={steps.map((s, i) => ({
      title: fill(s.title),
      body: fill(s.body),
      media: imageFor(i)
    }))} data-id="element-7512" data-mp="2uf92q1wxknbo" />)}

      <div className="mx-auto max-w-content px-6 pb-20 sm:pb-24 lg:px-10 lg:pb-32" data-id="element-7513" data-mp="2zd0vsz9bafhg">
        <div className="flex flex-col gap-3 border-t border-shell/25 pt-10 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-8 sm:gap-y-3" data-id="element-7514" data-mp="c4uqu57dlj4j">
          {/* Wie net heeft gezien hoe Sarah werkt, is klaar voor de salon: dat is de knop. */}
          {copy.cta && <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className="group inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full bg-shell px-7 text-[15px] text-ink transition-[background-color,transform] duration-150 ease-soft hover:bg-blush active:scale-[0.98]" data-id="element-7515" data-mp="l3ycngxxgcmq">
              {fill(copy.cta)}
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7516" data-mp="zv7auefwb6bh" />
            </button>}
          {copy.quizCta && <button type="button" onClick={openQuiz} aria-haspopup="dialog" className={`${quietLink} justify-center text-shell sm:justify-start`} data-id="element-7517" data-mp="18wokpf2pmbrq">
              {fill(copy.quizCta)}
            </button>}
          {copy.aboutCta && <Link to="/over-ons" className={`${quietLink} justify-center text-shell sm:justify-start`} data-id="element-7518" data-mp="1xepamwhqfklp">
              {fill(copy.aboutCta)}
              <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7519" data-mp="1ni2q68tbivk7" />
            </Link>}
          {copy.secondaryCta && <Link to="/behandelingen" className={`${quietLink} justify-center text-shell sm:justify-start`} data-id="element-7520" data-mp="19pwb6q1wiumc">
              {fill(copy.secondaryCta)}
              <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7521" data-mp="ztjfjmmsq6oy" />
            </Link>}
        </div>
        {copy.consultLine && <button type="button" onClick={() => openBooking(policy.consult.slug)} aria-haspopup="dialog" className="mt-4 inline-flex min-h-[44px] items-start gap-3 text-left text-[15px] leading-relaxed text-shell underline-offset-4 hover:underline" data-id="element-7522" data-mp="14prrys9lzlsf">
            <VideoIcon className="mt-1 h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-7523" data-mp="2spvjdhci4iuz" />
            {fill(copy.consultLine)}
          </button>}
      </div>
    </section>;
}
type SceneStep = {
  title: string;
  body: string;
  media: Media;
};

/**
 * De vastgezette scène. Het spoor is één scherm plus ± 0,8 scherm per stap hoog; daarin blijft
 * het beeld staan terwijl je scrolt. Alle stappen staan in de lijst (schermlezers lezen ze
 * allemaal); alleen de actieve is zichtbaar.
 */
function PinnedSteps({
  steps
}: {
  steps: SceneStep[];
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const {
    scrollYProgress
  } = useScroll({
    target: trackRef,
    offset: ['start start', 'end end']
  });
  useMotionValueEvent(scrollYProgress, 'change', v => {
    const next = stepAt(v, steps.length);
    setActive(prev => prev === next ? prev : next);
  });
  return <div ref={trackRef} style={{
    height: trackHeight(steps.length)
  }} className="relative mt-12 lg:mt-16" data-id="element-7524" data-mp="6iqxxxbcolbt">
      <div className={`sticky ${belowHeader} h-[calc(100svh-var(--header-offset,0px))]`} data-id="element-7525" data-mp="ajkpd0o04i9g">
        <div className="mx-auto grid h-full max-w-content grid-rows-[minmax(0,1fr)_auto] gap-6 px-6 py-6 lg:grid-cols-12 lg:grid-rows-1 lg:items-center lg:gap-16 lg:px-10 lg:py-10" data-id="element-7526" data-mp="fjfcct4nonp8">
          <StepFrame steps={steps} active={active} className="h-full min-h-0 lg:col-span-6 lg:h-[min(100%,780px)]" data-id="element-7527" data-mp="p8z0oeb519rh" />

          <div className="lg:col-span-6" data-id="element-7528" data-mp="u7k04bikfpev">
            {/* Waar je bent: één balkje per stap, dat meeloopt met het scrollen. */}
            <div aria-hidden="true" className="flex gap-2" data-id="element-7529" data-mp="yayih8tz8hvh">
              {steps.map((s, i) => <Segment key={`${i}-${s.title}`} progress={scrollYProgress} count={steps.length} index={i} data-id="element-7530" data-mp="137gk3zu0g03l" data-mpi={i} />)}
            </div>
            {/* Alle stappen op dezelfde plek gestapeld: de hoogte is die van de langste, niets verspringt. */}
            <ol className="mt-6 grid lg:mt-10" data-id="element-7531" data-mp="1as96nasvltq7">
              {steps.map((s, i) => {
              const isActive = i === active;
              return <motion.li key={`${i}-${s.title}`} className={`[grid-area:1/1] ${isActive ? '' : 'pointer-events-none'}`} initial={false} animate={{
                opacity: isActive ? 1 : 0,
                y: isActive ? 0 : i < active ? -12 : 12
              }} transition={{
                duration: 0.25,
                ease
              }} data-id="element-7532" data-mp="16qfbfi1g9hxf" data-mpi={i}>
                    <StepText index={i} title={s.title} body={s.body} data-id="element-7533" data-mp="nt4p0y0x2ize" />
                  </motion.li>;
            })}
            </ol>
          </div>
        </div>
      </div>
    </div>;
}

/** Zonder vastzetten: elke stap met zijn foto, onder elkaar. */
function StackedSteps({
  steps
}: {
  steps: SceneStep[];
}) {
  return <ol className="mx-auto mt-12 grid max-w-content gap-14 px-6 pb-14 lg:mt-16 lg:gap-20 lg:px-10 lg:pb-20" data-id="element-7534" data-mp="2d8km3f1kge8x">
      {steps.map((s, i) => <li key={`${i}-${s.title}`} className="grid gap-6 lg:grid-cols-12 lg:items-center lg:gap-16" data-id="element-7535" data-mp="3q508xp5lib38" data-mpi={i}>
          <MediaView media={s.media} sizes="(min-width: 1024px) 45vw, 100vw" className="aspect-[4/3] w-full overflow-hidden lg:col-span-6" data-id="element-7536" data-mp="3v4mldwdlnnaz" />
          <div className="lg:col-span-6" data-id="element-7537" data-mp="3h6fzlelxddro">
            <StepText index={i} title={s.title} body={s.body} data-id="element-7538" data-mp="3m36alnivhj68" />
          </div>
        </li>)}
    </ol>;
}

/**
 * Het kader staat stil; alleen de foto erin wisselt. De nieuwe opent als een gordijn van onderen
 * (de foto zelf valt zacht op zijn plek), terug scrollen sluit hem weer.
 */
function StepFrame({
  steps,
  active,
  className
}: {
  steps: SceneStep[];
  active: number;
  className: string;
}) {
  return <div className={`relative overflow-hidden rounded-[1.25rem] bg-clay ${className}`} data-id="element-7539" data-mp="8wd28hua4ftj">
      {steps.map((s, i) => <motion.div key={`${i}-${s.title}`} className="absolute inset-0" initial={false} animate={{
      clipPath: i <= active ? 'inset(0% 0% 0% 0%)' : 'inset(100% 0% 0% 0%)'
    }} transition={{
      duration: i === 0 ? 0 : 0.3,
      ease
    }} aria-hidden={i !== active} data-id="element-7540" data-mp="wl5ykfetcpoo" data-mpi={i}>
          <motion.div className="h-full w-full" initial={false} animate={{
        scale: i === active ? 1 : 1.05
      }} transition={{
        duration: 0.3,
        ease
      }} data-id="element-7541" data-mp="16o5edat1vbyc">
            <MediaView media={s.media} sizes="(min-width: 1024px) 45vw, 100vw" className="h-full w-full" data-id="element-7542" data-mp="wraiz0s2proa" />
          </motion.div>
        </motion.div>)}
    </div>;
}
function Segment({
  progress,
  count,
  index
}: {
  progress: MotionValue<number>;
  count: number;
  index: number;
}) {
  const fillX = useTransform(progress, v => segmentFill(v, count, index));
  return <span className="relative block h-0.5 flex-1 overflow-hidden bg-shell/25" data-id="element-7543" data-mp="e1kdnjqopni4">
      <motion.span style={{
      scaleX: fillX
    }} className="absolute inset-0 origin-left bg-shell" data-id="element-7544" data-mp="dzhr59oqqci5" />
    </span>;
}

/** Cijfer, titel en uitleg van een stap. De lijst is al genummerd voor schermlezers. */
function StepText({
  index,
  title,
  body
}: {
  index: number;
  title: string;
  body: string;
}) {
  return <>
      <div className="flex items-baseline gap-4" data-id="element-7545" data-mp="3cldnbfe6joti">
        <span aria-hidden="true" className="font-display text-[2.5rem] font-light leading-none text-blush lg:text-[4.5rem]" data-id="element-7546" data-mp="3cmp0u6xfgaxt">
          {index + 1}
        </span>
        <h3 className="font-display text-[2rem] font-light leading-none lg:text-[2.75rem]" data-id="element-7547" data-mp="3mjtdng5duu1y">{title}</h3>
      </div>
      <p className="mt-4 max-w-md text-[16px] leading-relaxed text-shell lg:mt-6 lg:text-[18px]" data-id="element-7548" data-mp="4d6pjx2dlsw2">{body}</p>
    </>;
}